<script setup lang="ts">
import { computed } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { IconSelector, IconCheck, IconX } from '@tabler/icons-vue'

interface Option {
  value: string | number | boolean
  label: string
}

interface Props {
  modelValue?: string | number | boolean | null
  options: Option[]
  label?: string
  placeholder?: string
  error?: string
  required?: boolean
  disabled?: boolean
  helperText?: string
  clearable?: boolean
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: undefined,
  placeholder: undefined,
  error: undefined,
  required: false,
  disabled: false,
  helperText: undefined,
  clearable: false,
  id: () => `select-${Math.random().toString(36).substring(2, 11)}`
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | boolean): void
  (e: 'change'): void
}>()

const selectedOption = computed(() => {
  if (props.modelValue === undefined || props.modelValue === null || props.modelValue === '') {
    return props.placeholder ? { value: '', label: props.placeholder } : null
  }
  return props.options.find(option => option.value === props.modelValue) || null
})

const handleChange = (option: Option | null) => {
  if (!option) {
    emit('update:modelValue', '')
  } else {
    emit('update:modelValue', option.value)
  }
  emit('change')
}

const clearSelection = () => {
  emit('update:modelValue', '')
  emit('change')
}
</script>

<template>
  <div class="space-y-1">
    <label
      v-if="label"
      :for="id"
      class="block text-sm font-medium text-gray-400 mb-1"
    >
      {{ label }} <span v-if="required" class="text-red-500">*</span>
    </label>

    <div class="relative">
      <Listbox
        :model-value="selectedOption"
        :disabled="disabled"
        v-bind="$attrs"
        @update:model-value="handleChange"
      >
        <div class="relative">
          <ListboxButton
            :id="id"
            :aria-label="label"
            :aria-required="required"
            class="relative w-full cursor-default rounded-lg bg-base-300 py-2 pl-3 pr-10 text-left border border-white/[0.06] text-gray-200 font-body text-sm outline-none focus:border-amber-400/30 focus:shadow-[0_0_24px_rgba(226,179,64,0.08)] transition-all"
            :class="{ 'border-red-500 focus:border-red-500': error }"
          >
            <span class="block truncate">{{ selectedOption?.label || placeholder || 'Vyberte...' }}</span>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <IconSelector class="h-5 w-5 text-gray-500" aria-hidden="true" />
            </span>
          </ListboxButton>

          <transition
            leave-active-class="transition ease-in duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <ListboxOptions
              class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-base-300 border border-white/[0.06] py-1 text-sm shadow-xl focus:outline-none"
            >
              <ListboxOption
                v-if="placeholder"
                :value="{ value: '', label: placeholder }"
                class="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-200 hover:bg-amber-400/[0.03]"
              >
                <span class="block truncate text-gray-500">{{ placeholder }}</span>
              </ListboxOption>
              <ListboxOption
                v-for="option in options"
                :key="String(option.value)"
                v-slot="{ active, selected }"
                :value="option"
                class="relative cursor-default select-none"
              >
                <div
                  :class="[
                    active ? 'bg-amber-400/[0.03] text-amber-400' : 'text-gray-200',
                    'relative cursor-default select-none py-2 pl-3 pr-9'
                  ]"
                >
                  <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                    {{ option.label }}
                  </span>
                  <span
                    v-if="selected"
                    :class="[
                      'text-amber-400',
                      'absolute inset-y-0 right-0 flex items-center pr-4'
                    ]"
                  >
                    <IconCheck class="h-5 w-5" aria-hidden="true" />
                  </span>
                </div>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
      
      <button
        v-if="clearable && modelValue !== undefined && modelValue !== null && modelValue !== ''"
        type="button"
        class="absolute inset-y-0 right-8 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        @click="clearSelection"
      >
        <IconX class="w-4 h-4" />
      </button>
    </div>

    <p v-if="error" class="text-sm text-red-400">
      {{ error }}
    </p>
    <p v-else-if="helperText" class="text-sm text-gray-500 dark:text-gray-400">
      {{ helperText }}
    </p>
  </div>
</template>
